import { z } from "zod";
import { EXPERIENCE_LEVEL_IDS } from "./data/experience.js";
import { FACTION_IDS, REPUTATION_PATH_IDS } from "@personnel/setting";

export const leviathanCaptainSchema = z.object({
  name: z.string(),
  concept: z.string(),
  /** Null until chosen. Factions carry no cost; only Princep depends on one. */
  faction: z.enum(FACTION_IDS).nullable(),

  /** See BuildMode: a fixed level allowance, a carried pool, or unbounded. */
  buildMode: z.enum(["level", "points", "cost"]),

  /**
   * Experience pool the player is working with, in "points" mode. Null in the
   * other modes, where the budget is fixed by the level or absent entirely.
   */
  experiencePoints: z.number().int().nullable().default(null),
  /** Required when buildMode is "level"; ignored otherwise. */
  experienceLevel: z.enum(EXPERIENCE_LEVEL_IDS).nullable(),

  attributes: z.object({
    tactical: z.number().int(),
    execution: z.number().int(),
    tasking: z.number().int(),
    resolve: z.number().int(),
    reserve: z.number().int(),
    /** Purchased Rank. Distinguished Career raises the effective Rank to 6. */
    rank: z.number().int(),
  }),

  /** Quality ids, in selection order. */
  qualities: z.array(z.string()),

  /**
   * Points of Essence held. Spends Experience but does not add to Cost, so it
   * is tracked separately from everything else the captain buys.
   */
  essence: z.number().int().default(0),

  /**
   * Points of Essence spent in play. Tracking only -- it has no bearing on
   * creation, but the Captain's Log carries it so the player can reconcile
   * Experience spent against Experience earned.
   */
  essenceUsed: z.number().int().default(0),

  /**
   * Renown earned in play. Costs nothing -- it sits outside Cost and
   * Experience -- and drives the captain's Reputation Tier.
   */
  renown: z.number().int().default(0),

  /** Chosen once the captain reaches Tier 2. Null below that, and inert there. */
  reputationPath: z.enum(REPUTATION_PATH_IDS).nullable().default(null),

  /** Print the rounded Cost on the card, to sit alongside published cards. */
  showRoundedCost: z.boolean().default(false),
});

export type LeviathanCaptain = z.infer<typeof leviathanCaptainSchema>;

export function createBlankCaptain(): LeviathanCaptain {
  return {
    name: "",
    concept: "",
    faction: null,
    buildMode: "level",
    experienceLevel: null,
    experiencePoints: null,
    attributes: {
      tactical: 0,
      execution: 0,
      tasking: 0,
      resolve: 0,
      reserve: 0,
      rank: 0,
    },
    qualities: [],
    essence: 0,
    essenceUsed: 0,
    renown: 0,
    reputationPath: null,
    showRoundedCost: false,
  };
}
